//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useCallback, useRef, useState } from 'react';
import { Close } from '@carbon/icons-react';
import { useTeamspaceContext } from '../../TeamspaceContext';
import TenantDataService from '../../utils/TenantDataService';
import AiDataService from '../../utils/AiDataService';
import { notifyIngestionTriggered } from './ingestionTriggerBus';
import { safeWarn } from '../../utils/securityUtils';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../UserAuth';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import { normalizeProvider } from '../../utils/integrationNormalizers';
import IntegrationForm from './IntegrationForm';
import type { IntegrationFormValues } from './integrationSchema';

interface EditIntegrationPanelProps {
  isVisible: boolean;
  onClose: () => void;
  integrationData: any;
  mode?: 'edit' | 'view';
  fetchIntegrations: () => void;
  integrations: any[];
}

const EditIntegrationPanel: React.FC<EditIntegrationPanelProps> = ({
  isVisible,
  onClose,
  integrationData,
  mode = 'edit',
  fetchIntegrations,
  integrations,
}) => {
  const { teamspaceId } = useTeamspaceContext();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useUserAuth();
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const tenantDataService = new TenantDataService(
    () => user,
    () => isAuthenticated,
  );
  const aiDataService = new AiDataService(
    () => user,
    () => isAuthenticated,
  );

  const handleSave = useCallback(
    async (values: IntegrationFormValues) => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      setIsSaving(true);

      try {
        const response = await tenantDataService.getTeamspaceIntegrations(
          teamspaceId || '',
        );
        const existingIntegrations = response.integrations || [];

        // Guard against two unsafe cases before attempting a replace-by-id
        // merge: (1) integrationData.id is falsy (e.g. a legacy record with
        // no id) — matching on a falsy id would hit every id-less entry in
        // existingIntegrations, and .map would overwrite all of them with
        // the same mergedIntegration; (2) the integration was deleted (in
        // another tab/session) between panel-open and Save — no entry
        // matches, .map is a silent no-op, and saveIntegrations would
        // "succeed" while discarding the user's edit. Both cases must be
        // treated as "cannot safely save this edit."
        const matchFound =
          integrationData.id &&
          existingIntegrations.some(
            (int: any) => int.id === integrationData.id,
          );

        if (!matchFound) {
          showSnackbar(t('error-updating-integration'), 'error');
          return;
        }

        const mergedIntegration = {
          ...values,
          id: integrationData.id,
          isenabled: integrationData.isenabled ?? true,
        };

        // Replace in place by id; every other integration (and its isenabled
        // flag) is left untouched — this differs deliberately from
        // SidePanel's create-flow sweep (Task 3), which disables everything
        // else. Preserving both branches is required: getting this wrong
        // silently disables a teamspace's working integration on an
        // unrelated edit.
        const allIntegrations = existingIntegrations.map((int: any) =>
          int.id === integrationData.id ? mergedIntegration : int,
        );

        await tenantDataService.saveIntegrations(teamspaceId || '', {
          integrations: allIntegrations,
        });

        // Re-trigger ingestion when the edited integration is (still) the
        // enabled one — mirrors the pre-redesign behavior of this panel and
        // SidePanel's create flow (Task 3). Without this, editing an
        // enabled integration's API key/model would silently never get
        // re-ingested. A failed ingestion call is only warned about, never
        // surfaced as a save error.
        if (mergedIntegration.isenabled) {
          try {
            const ingestResponse = await aiDataService.triggerIngestion(
              teamspaceId || '',
              'True',
            );
            notifyIngestionTriggered(teamspaceId || '');
            if (ingestResponse?.success === false) {
              safeWarn('AI integration ingestion returned warning:', {
                integrationId: mergedIntegration.id,
                integrationName: mergedIntegration.name,
                response: ingestResponse,
              });
            }
          } catch (ingestError) {
            safeWarn('AI integration ingestion failed after save:', {
              integrationId: mergedIntegration.id,
              integrationName: mergedIntegration.name,
              error: ingestError,
            });
          }
        }

        showSnackbar(
          `${t('integration')} ${values.name} ${t('updated-successfully')}`,
          'success',
        );
        fetchIntegrations();
        setTimeout(onClose, 1200);
      } catch (error) {
        console.error('Error saving integration:', error);
        showSnackbar(t('error-updating-integration'), 'error');
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [
      integrationData,
      teamspaceId,
      tenantDataService,
      aiDataService,
      showSnackbar,
      t,
      onClose,
      fetchIntegrations,
    ],
  );

  if (!isVisible || !integrationData) return null;

  return (
    <>
      <div
        role="presentation"
        id="id-edit-integration-backdrop"
        data-testid="edit-integration-backdrop"
        className="fixed inset-0 top-12 z-40 bg-black/20"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
      />
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={closeSnackbar}
      />
      <div
        className="fixed bottom-0 right-0 top-12 z-50 flex w-[502px] flex-col border-l border-[#d3d9de] bg-white shadow-xl dark:border-gray-700 dark:bg-[#262626]"
        id="id-edit-integration-panel"
        data-testid="edit-integration-panel"
      >
        <div
          className="flex items-center justify-between border-b border-gray-200 bg-[#f2f4f5] p-4 dark:border-gray-700 dark:bg-[#393939]"
          id="id-edit-integration-header"
          data-testid="edit-integration-header"
        >
          <h2 className="text-[16px] font-semibold text-gray-700 dark:text-gray-300">
            {mode === 'view' ? t('view-integration') : t('edit-integration')}
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
            aria-label="Close integration panel"
          >
            <Close size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <IntegrationForm
            mode={mode}
            initialData={{
              ...integrationData,
              provider: normalizeProvider(
                integrationData.provider || '',
              ) as any,
            }}
            isNameUnique={(name, originalName) =>
              !integrations.some(
                (int) =>
                  int.name.toLowerCase() === name.toLowerCase() &&
                  (!originalName ||
                    int.name.toLowerCase() !== originalName.toLowerCase()),
              )
            }
            aiDataService={aiDataService}
            teamspaceId={teamspaceId || undefined}
            onSubmit={handleSave}
            onCancel={onClose}
            isSaving={isSaving}
          />
        </div>
      </div>
    </>
  );
};

export default EditIntegrationPanel;
